import { LOGIN_EMAIL_REGEX } from "./constants.js";
import { extractEmailDomain, normalizeEmailDomain } from "./emailAndEnvUtils.js";

/**
 * GET /api/v1/customers expands each identity_providers row into one config per email_domains value.
 * Same Cognito app / same sign-in path then appears multiple times with different email_domain only.
 * The login card should offer one control per distinct IdP row (identity_provider_id), not one per domain.
 */
function dedupeSignInOptionsForPicker(configs) {
  if (!Array.isArray(configs) || configs.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const c of configs) {
    const rawId = c?.identity_provider_id ?? c?.identityProviderId;
    const idKey = rawId != null && String(rawId).trim() !== "" ? `id:${String(rawId).trim()}` : "";
    const key = idKey || [
      String(c?.provider_name || "").toLowerCase(),
      String(c?.idp_type || "").toLowerCase(),
      String(c?.provider_type || "").toLowerCase(),
      c?.local_cognito_enabled === true ? "1" : "0",
    ].join("\0");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

/**
 * Config rows for the current env whose email_domain matches the user's address, plus wildcard rows
 * (email_domain null/empty = any domain for that IdP).
 */
export function filterConfigsMatchingEmailDomain(configs, emailValue) {
  if (!Array.isArray(configs) || configs.length === 0) return [];
  const trimmed = String(emailValue || "").trim();
  if (!trimmed || !LOGIN_EMAIL_REGEX.test(trimmed)) return [];
  const userDomain = normalizeEmailDomain(extractEmailDomain(trimmed));
  if (!userDomain) return [];
  return configs.filter((c) => {
    const ed = c.email_domain;
    if (ed == null || String(ed).trim() === "") return true;
    return normalizeEmailDomain(ed) === userDomain;
  });
}

/** True when sign-in should use Cognito username/password (not federated redirect). */
export function signInOptionUsesLocalPassword(config) {
  const idpType = String(config?.idp_type || "").trim().toLowerCase();
  return idpType === "cognito" && config?.local_cognito_enabled === true;
}

/**
 * IdP rows to offer for login: domain-scoped options first (including wildcards).
 * When nothing matches the typed domain, fall back to local Cognito IdPs in the env only.
 */
export function resolveSignInOptionsForEmail(envConfigs, emailValue) {
  if (!Array.isArray(envConfigs) || envConfigs.length === 0) return [];
  const matched = filterConfigsMatchingEmailDomain(envConfigs, emailValue);
  const scoped = dedupeSignInOptionsForPicker(matched);
  if (scoped.length > 0) return scoped;
  const allDeduped = dedupeSignInOptionsForPicker(envConfigs);
  return allDeduped.filter((c) => signInOptionUsesLocalPassword(c));
}

export function signInOptionPickerKey(config) {
  const rawId = config?.identity_provider_id ?? config?.identityProviderId;
  if (rawId != null && String(rawId).trim() !== "") {
    return `idp:${String(rawId).trim()}:${config?.local_cognito_enabled === true ? "local" : "fed"}`;
  }
  return [
    String(config?.provider_name || ""),
    String(config?.idp_type || ""),
    String(config?.provider_type || ""),
    config?.local_cognito_enabled === true ? "local" : "federated",
  ].join("|");
}

export function formatIdpLoginButtonLabel(config) {
  const raw = String(config?.idp_display_name || "").trim();
  const typeHint = String(config?.provider_type || config?.idp_type || "")
    .trim()
    .replace(/_/g, " ");
  const providerLabel = String(config?.provider_name || "").trim();
  const fallback = config?.local_cognito_enabled === true
    ? (typeHint && !/^cognito$/i.test(typeHint) ? typeHint : "Cognito")
    : (providerLabel || typeHint || "SSO");
  const name = raw || fallback;
  if (
    /^(sign\s*in|log\s*in)\s+with\s+/i.test(name)
    || /^continue\s+with\s+/i.test(name)
    || /^use\s+/i.test(name)
  ) {
    return name;
  }
  return `Sign in with ${name}`;
}
